/**
 * P3-38: ShatterTCBtn —— 时间水晶粉碎按钮。
 *
 * 对齐 `js/time.js:1170-1476`：
 * - 主按钮点击粉碎 1 年
 * - 链接：nextCycle / previousCycle / tenEras / custom
 * - 最右可见链接加 `rightestLink`
 * - heat > heatMax 时名称追加 warning
 */
import { useMemo, type MouseEvent } from "react";
import { getGame } from "../../bridge/GameBridge";
import type { GamePage } from "../../bridge/GameBridge";
import { GameButton } from "../../components/GameButton";
import type { GameButtonLinkModel } from "../../components/GameButton";
import { useButtonModel, makeModernTooltip, type ModernButtonController } from "../bonfire/useButtonModel";

interface ShatterLinkSnapshot {
  title: string;
  quantity: number;
  visible: boolean;
}

interface ShatterSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  nextCycle: ShatterLinkSnapshot;
  previousCycle: ShatterLinkSnapshot;
  tenEras: ShatterLinkSnapshot;
  custom: ShatterLinkSnapshot | null;
}

function toLink(toggle: Record<string, unknown> | undefined): ShatterLinkSnapshot | null {
  if (!toggle) {
    return null;
  }
  return {
    title: String(toggle.title ?? ""),
    quantity: Number(toggle.quantity ?? 0),
    visible: Boolean(toggle.visible),
  };
}

function toSnapshot(model: Record<string, unknown>): ShatterSnapshot {
  return {
    name: String(model.name ?? ""),
    description: String(model.description ?? ""),
    visible: Boolean(model.visible),
    enabled: Boolean(model.enabled),
    nextCycle: toLink(model.nextCycleLink as Record<string, unknown>) ?? { title: "", quantity: 0, visible: false },
    previousCycle: toLink(model.previousCycleLink as Record<string, unknown>) ?? { title: "", quantity: 0, visible: false },
    tenEras: toLink(model.tenErasLink as Record<string, unknown>) ?? { title: "", quantity: 0, visible: false },
    custom: toLink(model.customLink as Record<string, unknown>),
  };
}

function snapEq(a: ShatterSnapshot | null, b: ShatterSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (
    a.name !== b.name ||
    a.description !== b.description ||
    a.visible !== b.visible ||
    a.enabled !== b.enabled ||
    a.nextCycle.title !== b.nextCycle.title ||
    a.nextCycle.quantity !== b.nextCycle.quantity ||
    a.nextCycle.visible !== b.nextCycle.visible ||
    a.previousCycle.title !== b.previousCycle.title ||
    a.previousCycle.quantity !== b.previousCycle.quantity ||
    a.previousCycle.visible !== b.previousCycle.visible ||
    a.tenEras.title !== b.tenEras.title ||
    a.tenEras.quantity !== b.tenEras.quantity ||
    a.tenEras.visible !== b.tenEras.visible
  ) {
    return false;
  }
  if ((a.custom === null) !== (b.custom === null)) {
    return false;
  }
  if (a.custom && b.custom) {
    if (
      a.custom.title !== b.custom.title ||
      a.custom.quantity !== b.custom.quantity ||
      a.custom.visible !== b.custom.visible
    ) {
      return false;
    }
  }
  return true;
}

const CONTROLLER_OPTS = { prices: [{ name: "timeCrystal", val: 1 }] };

export default function ShatterTCBtn() {
  const controller = useMemo<ModernButtonController | null>(() => {
    const ControllerClass = (
      window as unknown as {
        classes?: { ui?: { time?: { ShatterTCBtnController?: new (game: GamePage) => ModernButtonController } } };
      }
    ).classes?.ui?.time?.ShatterTCBtnController;
    const game = getGame();
    if (typeof ControllerClass !== "function" || !game) {
      return null;
    }
    return new ControllerClass(game);
  }, []);

  const { snap, modelRef } = useButtonModel(controller, () => CONTROLLER_OPTS, toSnapshot, snapEq);

  const getTooltip = makeModernTooltip(() => controller, () => CONTROLLER_OPTS);

  if (!snap || !snap.visible) {
    return null;
  }

  const allLinks: { key: string; snap: ShatterLinkSnapshot }[] = [
    { key: "tenEras", snap: snap.tenEras },
    { key: "previousCycle", snap: snap.previousCycle },
    { key: "nextCycle", snap: snap.nextCycle },
  ];
  if (snap.custom) {
    allLinks.push({ key: "custom", snap: snap.custom });
  }

  const firstVisibleIndex = allLinks.findIndex((l) => l.snap.visible);

  // eslint-disable-next-line react-hooks/refs -- handler 在点击时读取 modelRef，符合旧 Button 事件模型
  const links: GameButtonLinkModel[] = allLinks.map((l, index) => ({
    title: l.snap.title,
    cssClass: index === firstVisibleIndex ? "rightestLink" : undefined,
    visible: l.snap.visible,
    handler: () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, react-hooks/refs -- 点击时读取最新 model
      (controller as any)?.doShatterAmt?.(modelRef.current, l.snap.quantity);
      getGame()?.render();
    },
  }));

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      getTooltip={getTooltip}
      onClick={(event: MouseEvent) => {
        controller?.buyItem?.(modelRef.current, event);
        getGame()?.render();
      }}
      links={links}
    />
  );
}
